import { Button, Text } from '$components'
import { XIcon, CompassIcon } from '$components'
import { cn } from '$components'
import { motion, AnimatePresence } from 'framer-motion'
import { useOnboardingTutorial } from '../hooks/use-onboarding-tutorial'
import type { OnboardingWizardConfig } from '../types'
import { useModalRegistry } from '@luuna/shared'

export type OnboardingTutorialProps = {
  config: OnboardingWizardConfig
}

export function OnboardingTutorial({ config }: OnboardingTutorialProps) {
  const {
    currentStep,
    currentStepIndex,
    steps,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    toggleVisibility,
    isVisible,
    completedStepsCount,
    isTutorialCompleted,
    completeTutorialFlow,
    isCompletingTutorial,
  } = useOnboardingTutorial(config)

  const { isAnyModalOpen } = useModalRegistry()

  const hasStarted = currentStepIndex > 0 || completedStepsCount > 0

  // Don't render the tutorial if the user has already completed it
  if (isTutorialCompleted) {
    return null
  }

  return (
    <div className="relative">
      {/* Onboarding Indicator Button */}
      <button
        onClick={toggleVisibility}
        className={cn(
          'flex justify-center items-center rounded-card-sm bg-surface-muted border border-transparent hover:border-border-strong p-stack min-w-12 min-h-12',
          {
            'border border-brand hover:border-brand bg-brand-subtle': hasStarted,
          },
        )}
        aria-label={isVisible ? 'Collapse onboarding tutorial' : 'Expand onboarding tutorial'}
      >
        {hasStarted
          ? (
            <div className="flex flex-col items-center gap-stack-dense">
              <CompassIcon className="size-5 text-fg-link" />

              <Text type="xs" className="font-medium text-fg-link">
                {currentStepIndex + 1}/{steps.length}
              </Text>
            </div>

          )
          : <CompassIcon className="size-5 text-fg" />
        }
      </button>

      {/* Expanded Popover */}
      <AnimatePresence>
        {!isAnyModalOpen && isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
            className="fixed left-4 bottom-4 w-[500px] z-20 bg-surface border border-border rounded-card-sm shadow-popover overflow-hidden"
          >
            <div className="p-stack-roomy pb-stack">
              <div className="flex items-center justify-between">
                <Text
                  type="xs"
                  className="text-fg-link uppercase"
                >
                  Getting Started
                </Text>

                <div className="flex items-center gap-stack-tight">
                  <Text type="xs" className="text-fg-link">
                    Step {currentStepIndex + 1} of {steps.length}
                  </Text>

                  <button
                    type="button"
                    onClick={toggleVisibility}
                    className="p-stack-tight border border-transparent hover:border-border hover:bg-surface rounded-surface transition-colors"
                  >
                    <XIcon className="size-4 text-fg-muted" />
                  </button>
                </div>
              </div>

              {/* Step indicators */}
              <div className="flex items-center gap-stack-dense mt-stack">
                {steps.map((step) => (
                  <div
                    key={step.id}
                    className={cn(
                      'flex-1 h-1.5 rounded-pill transition-all duration-control',
                      {
                        'bg-success': step.isCompleted,
                        'bg-brand': step.isActive && !step.isCompleted,
                        'bg-border-strong': !step.isActive && !step.isCompleted,
                      },
                    )}
                  />
                ))}
              </div>
            </div>

            {currentStep && (
              <div className="px-stack-roomy pb-stack-roomy flex flex-col gap-stack-tight">
                {/* Current step info */}
                <Text as="h3">
                  {currentStep.title}
                </Text>

                <Text type="s">
                  {currentStep.description}
                </Text>

                {/* Controls */}
                <div className="flex items-center justify-end gap-stack-tight pt-stack-tight">
                  <Button
                    variant="ghost"
                    onClick={previousStep}
                    disabled={isFirstStep}
                  >
                    Previous
                  </Button>

                  <Button
                    onClick={isLastStep ? completeTutorialFlow : nextStep}
                    isLoading={isLastStep && isCompletingTutorial}
                    disabled={isLastStep && isCompletingTutorial}
                  >
                    {isLastStep ? 'Complete' : 'Next'}
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
